import { useCallback, useEffect, useState } from "react";
import { MdAnnouncement } from "react-icons/md";
import Modal from "react-responsive-modal";
import { useAuth } from "../../hooks/auth";
import { useRefresh } from "../../hooks/refresh";
import { useToast } from "../../hooks/toast";
import api from "../../services/api";
import refreshToken from "../../utils/refreshToken";

import {
    Content,
    ButtonsContainer,
    ConfirmButton,
    CancelButton
} from './styles';
import ScheduleType from "../../pages/Schedules/ScheduleType";

interface IDateSelected {
    scheduleId: number;
    schedule: string;
}

interface IModalToDemarcateSchedule {
    open: boolean;
    dateSelected: IDateSelected;
    functionToCloseModal(): void;
}

export default function ModalToDemarcateSchedule({ open, dateSelected, functionToCloseModal }: IModalToDemarcateSchedule) {
    const { dateToExpires, access_token, user } = useAuth();
    const { addToast } = useToast();
    const { handleRefresh } = useRefresh();

    const canManageSchedules = user?.role !== "consultant";

    const [loadingRequestToCancelSchedule, setLoadingRequestToCancelSchedule] = useState(false);

    useEffect(() => {
        if (!open || canManageSchedules) {
            return;
        }
        addToast({
            title: "Sem permissão para alterar a agenda",
            type: "error",
        });
        functionToCloseModal();
    }, [open, canManageSchedules, addToast, functionToCloseModal]);

    const handleCancelSchedule = useCallback(async () => {
        if (!canManageSchedules) {
            addToast({
                title: "Sem permissão para alterar a agenda",
                type: "error",
            });
            return;
        }

        setLoadingRequestToCancelSchedule(true);

        try {
            const token = await refreshToken(dateToExpires, access_token);

            api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token}`;

            const response = await api.put(`/api/adm/schedules/${dateSelected.scheduleId}`, {
                schedule: dateSelected.schedule,
                status: ScheduleType.CANCELED
            });

            if (!!response.data.success) {
                addToast({
                    title: "Agendamento cancelado",
                    type: 'success',
                    description: ''
                });

                handleRefresh();
                functionToCloseModal();
            }

            setLoadingRequestToCancelSchedule(false);
        } catch (err: any) {
            if (!!err.response?.data.message) {
                addToast({
                    title: err.response.data.message,
                    type: "error",
                });
            }

            setLoadingRequestToCancelSchedule(false);
        }
    }, [dateSelected, dateToExpires, access_token, addToast, handleRefresh, functionToCloseModal, canManageSchedules]);

    const formatScheduleDate = (schedule: string | undefined) => {
        if (!schedule) return "Data não disponível";

        const [date, time] = schedule.split('|');
        if (!date || !time) return "Formato inválido";

        const [year, month, day] = date.split('-');
        if (!year || !month || !day) return "Data inválida";

        return `${day}/${month}/${year} às ${time}`;
    };

    return (
        <Modal open={open} onClose={functionToCloseModal} classNames={{
            modal: "remove-photo-modal"
        }}>
            <Content>
                <MdAnnouncement size={40} color="#666666" />

                <p>Tem certeza que deseja cancelar esta visita?</p>

                <ButtonsContainer>
                    <ConfirmButton
                        onClick={!!loadingRequestToCancelSchedule ? () => { } : handleCancelSchedule}
                        isLoading={loadingRequestToCancelSchedule}
                    >
                        {!!loadingRequestToCancelSchedule ? "Carregando..." : "Sim, Cancelar"}
                    </ConfirmButton>
                    <CancelButton onClick={functionToCloseModal}>Não</CancelButton>
                </ButtonsContainer>
            </Content>
        </Modal>
    );
}